import React from 'react';
import { Exception } from '../types';

interface Props {
  exceptions: Exception[] | null | undefined;
}

const severityConfig = {
  CRITICAL: {
    icon: '🔴',
    bg: 'bg-[#EF4444]/10',
    border: 'border-[#EF4444]/30',
    badge: 'bg-[#EF4444]/20 text-[#F87171]',
    label: 'CRITICAL'
  },
  HIGH: {
    icon: '🟠',
    bg: 'bg-[#F59E0B]/10',
    border: 'border-[#F59E0B]/30',
    badge: 'bg-[#F59E0B]/20 text-[#FBBF24]',
    label: 'HIGH'
  },
  MEDIUM: {
    icon: '🟡',
    bg: 'bg-[#EAB308]/10',
    border: 'border-[#EAB308]/30',
    badge: 'bg-[#EAB308]/20 text-[#FDE047]',
    label: 'MEDIUM'
  },
  LOW: {
    icon: '🔵',
    bg: 'bg-[#3B82F6]/10',
    border: 'border-[#3B82F6]/30',
    badge: 'bg-[#3B82F6]/20 text-[#93C5FD]',
    label: 'LOW'
  }
};

export default function ExceptionPanel({ exceptions }: Props) {
  if (!exceptions) {
    return (
      <div className="bg-[#1A1D27] rounded-lg p-4 border border-gray-800">
        <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Exceptions</div>
        <div className="text-gray-600 text-sm text-center py-2">Evaluating...</div>
      </div>
    );
  }

  if (exceptions.length === 0) {
    return (
      <div className="bg-[#1A1D27] rounded-lg p-4 border border-[#10B981]/30">
        <div className="flex items-center gap-2 text-[#10B981]">
          <span className="text-lg">✅</span>
          <div>
            <div className="font-medium text-sm">No exceptions</div>
            <div className="text-xs text-[#6EE7B7] mt-0.5">Deal is clean — all thresholds within limits</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1A1D27] rounded-lg p-4 border border-gray-800">
      <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">
        Exceptions ({exceptions.length})
      </div>
      <div className="space-y-2">
        {exceptions.map((ex, i) => {
          const config = severityConfig[ex.severity] || severityConfig.HIGH;
          return (
            <div
              key={i}
              className={`rounded-lg p-3 border ${config.bg} ${config.border}`}
            >
              <div className="flex items-start gap-2">
                <span className="text-base leading-none mt-0.5">{config.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-mono font-semibold px-1.5 py-0.5 rounded ${config.badge}`}>
                      {config.label}
                    </span>
                    <span className="text-xs font-mono text-gray-400">{ex.exceptionCode}</span>
                  </div>
                  <div className="text-sm text-gray-200 mb-1">{ex.exceptionMessage}</div>
                  <div className="text-xs text-gray-500">
                    Action: <span className="text-gray-400">{ex.action || (ex as any).requiredAction}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
